import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import type { ComboboxOption } from '@/components/ui/Combobox';
import type { City, Intake, Product, Project } from '@/lib/types';

/**
 * ── The option lists the /projects INLINE editors write against ─────────────
 *
 * Split out of `ProjectInlineCells.tsx` so that file exports components and
 * nothing else — fast refresh gives up on a module mixing the two, which turns
 * every edit to a cell into a full reload.
 *
 * ⚠️ **These are deliberately NOT the lists behind the page's filter bank, and
 * the duplication is the point.** A filter over `/projects` must offer RETIRED
 * products and cities: a project keeps reading a product or a city retired after
 * it was sold, so a filter narrowed to active rows could never find those
 * projects at all. A picker that WRITES has the exact opposite requirement — an
 * inactive id is a `422`. One list cannot answer both questions, and reusing the
 * filter's list here would fill a dropdown with guaranteed refusals.
 *
 * Regions are the one master shared between the two, because
 * `useReferenceOptions` already sends `?isActive=true` — which is what a write
 * needs — so the page passes its own copy straight through.
 */

/** One page of picker options. `/products` searches server-side beyond it. */
const OPTION_PER_PAGE = 100;
/** `/cities` caps `perPage` at 200 and defaults to 50. Ask for the ceiling. */
const CITY_PER_PAGE = 200;

/**
 * How many distinct cycles' intake lists one page will fetch.
 *
 * The intake picker is scoped per row, so its option list depends on THAT row's
 * cycle — but the requests are keyed by CYCLE, not by row, so a page of 100
 * projects sharing one cycle costs one request. Cycles are business years (four
 * rows in the master at the last measurement), and the page's Cycle filter
 * defaults to the one running today, so in practice this is one request.
 *
 * ⚠️ **The cap exists because "in practice" is a statement about today's data.**
 * A fan-out bounded only by what happens to be on screen is the shape this
 * codebase already warns about for permission-set membership.
 * ⚠️ *SUPERSEDED: "The limiter is 120 requests per 60 seconds shared across the
 * office NAT."* It is **12,000/60s** (corrected 2026-09-17). The cap is still
 * right — a fan-out with no declared ceiling is the wrong shape whatever the
 * limiter allows — but it may not be argued from that figure. A cycle past the
 * cap reports `skipped` and its cell points at the Edit modal, which fetches for
 * one record. **This has never fired** — there are not enough cycles for it to,
 * which is exactly why it must not be deleted as unused.
 */
const MAX_INTAKE_CYCLES = 8;

export interface PickerSource {
  options: ComboboxOption[];
  loading: boolean;
  failed: boolean;
  /** The server holds more rows than were loaded, so the list is short. */
  truncated: boolean;
  total: number;
}

const EMPTY_SOURCE: PickerSource = {
  options: [],
  loading: false,
  failed: false,
  truncated: false,
  total: 0,
};

/**
 * The two masters an inline write needs, at their WRITE-time filtering, fetched
 * once for the page.
 *
 * `enabled` is `canUpdate`: a reader has nothing to fill a picker with, and the
 * rate limiter is shared across the office.
 */
export function useProjectWriteOptions(enabled: boolean): {
  products: PickerSource;
  cities: PickerSource;
  /**
   * The ids a `cityIds` write may legally contain, or **`null` when nobody
   * knows**.
   *
   * ⚠️ **`null` and an empty Set are different facts and must never be
   * coalesced.** An empty Set says "the master holds no active city"; `null`
   * says "the list did not arrive". A `?? new Set()` here would turn every
   * failed read into a client-side refusal naming the reader's perfectly live
   * cities as retired — an accusation manufactured out of a request that never
   * came back, on the one screen where somebody is about to act on it.
   */
  activeCityIds: ReadonlySet<string> | null;
} {
  const [products, setProducts] = useState<Product[]>([]);
  const [productsMeta, setProductsMeta] = useState({ truncated: false, total: 0, failed: false });
  const [productsLoading, setProductsLoading] = useState(enabled);

  const [cities, setCities] = useState<City[]>([]);
  const [citiesMeta, setCitiesMeta] = useState({ truncated: false, total: 0, failed: false });
  const [citiesLoading, setCitiesLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listProducts({ active: true, perPage: OPTION_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setProducts(result.data);
        setProductsMeta({
          truncated: result.meta.totalPages > 1,
          total: result.meta.total,
          failed: false,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setProducts([]);
        setProductsMeta({ truncated: false, total: 0, failed: true });
      })
      .finally(() => {
        if (!controller.signal.aborted) setProductsLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listCities({ isActive: true, perPage: CITY_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCities(result.data);
        setCitiesMeta({
          truncated: result.meta.totalPages > 1,
          total: result.meta.total,
          failed: false,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCities([]);
        setCitiesMeta({ truncated: false, total: 0, failed: true });
      })
      .finally(() => {
        if (!controller.signal.aborted) setCitiesLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  const productSource = useMemo<PickerSource>(
    () => ({
      options: products.map((product) => ({
        value: String(product.id),
        // `productName`, not `.name` — the catalog keys differ from the CRM ones.
        label: product.productName,
        ...(product.productCode ? { hint: product.productCode } : {}),
      })),
      loading: enabled && productsLoading,
      failed: productsMeta.failed,
      truncated: productsMeta.truncated,
      total: productsMeta.total,
    }),
    [products, enabled, productsLoading, productsMeta],
  );

  const citySource = useMemo<PickerSource>(
    () => ({
      options: cities.map((city) => ({
        value: String(city.id),
        // The country is part of the LABEL here, not a hint: a MultiCombobox
        // chip renders the label alone, and "Birmingham" is two cities.
        label: city.country ? `${city.name} · ${city.country.name}` : city.name,
        ...(city.country ? {} : { hint: 'No country recorded' }),
      })),
      loading: enabled && citiesLoading,
      failed: citiesMeta.failed,
      truncated: citiesMeta.truncated,
      total: citiesMeta.total,
    }),
    [cities, enabled, citiesLoading, citiesMeta],
  );

  const activeCityIds = useMemo<ReadonlySet<string> | null>(() => {
    if (!enabled || citiesLoading || citiesMeta.failed) return null;
    return new Set(cities.map((city) => String(city.id)));
  }, [enabled, citiesLoading, citiesMeta.failed, cities]);

  return { products: productSource, cities: citySource, activeCityIds };
}

interface CycleIntakes {
  intakes: Intake[];
  failed: boolean;
  truncated: boolean;
  total: number;
}

export interface IntakeSource extends PickerSource {
  /** This cycle was never requested — the fetch cap was reached first. */
  skipped: boolean;
}

/**
 * Intake lists, keyed by CYCLE rather than by row.
 *
 * The scoping is what makes the bad state unreachable rather than explained: an
 * intake outside the row's cycle is never offered, so the `422` naming
 * `intakeId` cannot be reached from the intake control at all.
 *
 * ⚠️ **A request aborted mid-flight is un-marked so it is asked again.** Without
 * that, a re-render while a cycle's intakes were in the air leaves that cycle
 * marked "requested" with nothing ever arriving, and every row on it reads as
 * permanently loading — a spinner that is not waiting for anything, which looks
 * exactly like a slow network and never resolves.
 */
export function useIntakesByCycle(
  rows: readonly Project[],
  enabled: boolean,
): (cycleId: number | null) => IntakeSource {
  const wantedKey = useMemo(() => {
    const ids = new Set<number>();
    for (const row of rows) if (row.cycle) ids.add(row.cycle.id);
    return [...ids].sort((a, b) => a - b).join(',');
  }, [rows]);

  const [byCycle, setByCycle] = useState<Record<number, CycleIntakes>>({});
  /** Cycles already asked for, so a re-render does not ask twice. */
  const requested = useRef(new Set<number>());

  useEffect(() => {
    if (!enabled || wantedKey === '') return;
    // Copied into the effect: the cleanup below must act on the same Set this
    // run marked, not on whatever the ref points at whenever it happens to run.
    const asked = requested.current;
    const ids = wantedKey.split(',').map(Number).slice(0, MAX_INTAKE_CYCLES);
    const missing = ids.filter((id) => !asked.has(id));
    if (missing.length === 0) return;
    for (const id of missing) asked.add(id);

    const controller = new AbortController();
    const settled = new Set<number>();

    for (const id of missing) {
      api
        .listIntakes({ cycleId: id, perPage: OPTION_PER_PAGE }, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          setByCycle((current) => ({
            ...current,
            [id]: {
              intakes: result.data,
              failed: false,
              truncated: result.meta.totalPages > 1,
              total: result.meta.total,
            },
          }));
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setByCycle((current) => ({
            ...current,
            [id]: { intakes: [], failed: true, truncated: false, total: 0 },
          }));
        })
        .finally(() => {
          if (!controller.signal.aborted) settled.add(id);
        });
    }

    return () => {
      controller.abort();
      // Anything still in the air was never answered, so it is not "requested"
      // in any sense that should stop the next run from asking.
      for (const id of missing) if (!settled.has(id)) asked.delete(id);
    };
  }, [enabled, wantedKey]);

  /** Ids on screen the cap kept us from asking about. */
  const overCap = useMemo(() => {
    if (wantedKey === '') return new Set<number>();
    return new Set(wantedKey.split(',').map(Number).slice(MAX_INTAKE_CYCLES));
  }, [wantedKey]);

  return useCallback(
    (cycleId: number | null): IntakeSource => {
      if (cycleId === null || !enabled) return { ...EMPTY_SOURCE, skipped: false };
      if (overCap.has(cycleId)) return { ...EMPTY_SOURCE, skipped: true };
      const entry = byCycle[cycleId];
      // Absent means in flight: the effect marks a cycle requested before any
      // response can land, and only a response writes an entry.
      if (!entry) return { ...EMPTY_SOURCE, loading: true, skipped: false };
      return {
        options: entry.intakes.map((intake) => ({
          value: String(intake.id),
          // `intakeName`, not `.name`.
          label: intake.intakeName,
          // Plain 'YYYY-MM-DD' strings, shown verbatim — never parsed to a Date.
          hint: `${intake.startDate} – ${intake.endDate}`,
        })),
        loading: false,
        failed: entry.failed,
        truncated: entry.truncated,
        total: entry.total,
        skipped: false,
      };
    },
    [byCycle, overCap, enabled],
  );
}
